import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type { OriginalAttributes, VectorFeatureRecord, VectorLayerModel, WorkingGeometryType } from '../core/types';
import { defaultVectorLayerStyle, withLayerFeatures } from './layers';

const randomId = (): string => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const geometryTypeFor = (geometry: Geometry): WorkingGeometryType | null => {
  const type = geometry.getType();
  return type === 'Point' || type === 'LineString' || type === 'Polygon' ? type : null;
};

export function createWorkingLayer(name: string, geometryType: WorkingGeometryType, id = `work-${randomId()}`): VectorLayerModel {
  return {
    id,
    kind: 'working',
    name: name.trim() || `Capa de ${geometryType === 'Point' ? 'puntos' : geometryType === 'LineString' ? 'líneas' : 'polígonos'}`,
    sourceName: 'Creada en CASUR GeoStudio',
    sourceFormat: 'WORK',
    sourceCrs: 'EPSG:3857',
    assumedCrs: false,
    geometryTypes: [],
    attributeFields: ['Nombre', 'Observación'],
    features: [],
    featuresById: new Map(),
    extent: null,
    visible: true,
    opacity: 1,
    locked: false,
    order: 0,
    style: { ...defaultVectorLayerStyle },
    warnings: [],
    workingGeometryType: geometryType,
    scopeMode: 'none',
  };
}

export const isGeometryCompatible = (layer: VectorLayerModel, geometry: Geometry): boolean =>
  layer.kind === 'working' && geometryTypeFor(geometry) === layer.workingGeometryType;

export function addWorkingFeature(
  layer: VectorLayerModel,
  geometry: Geometry,
  options: { id?: string; createdAt?: string; name?: string; observation?: string } = {},
): { layer: VectorLayerModel; record: VectorFeatureRecord } {
  const geometryType = geometryTypeFor(geometry);
  if (layer.kind !== 'working' || !geometryType || geometryType !== layer.workingGeometryType) {
    throw new Error('La geometría no es compatible con la capa de trabajo activa.');
  }
  const id = options.id ?? `${layer.id}/feature-${randomId()}`;
  if (layer.featuresById.has(id)) throw new Error(`Ya existe un objeto con ID ${id}.`);
  const feature = new Feature({ geometry });
  feature.set('genericFeatureId', id, true);
  const attributes: OriginalAttributes = { Nombre: options.name ?? '', 'Observación': options.observation ?? '' };
  const record: VectorFeatureRecord = {
    id,
    feature,
    attributes,
    projectMetadata: { createdAt: options.createdAt ?? new Date().toISOString(), geometryType },
  };
  return { layer: withLayerFeatures(layer, [...layer.features, record]), record };
}

export function updateWorkingFeatureMetadata(layer: VectorLayerModel, featureId: string, name: string, observation: string): VectorLayerModel {
  if (layer.kind !== 'working') return layer;
  const records = layer.features.map((record) => record.id === featureId
    ? { ...record, attributes: { ...record.attributes, Nombre: name, 'Observación': observation } }
    : record);
  return withLayerFeatures(layer, records);
}

export function deleteWorkingFeatures(layer: VectorLayerModel, featureIds: ReadonlySet<string>): VectorLayerModel {
  if (layer.kind !== 'working' || !featureIds.size) return layer;
  return withLayerFeatures(layer, layer.features.filter(({ id }) => !featureIds.has(id)));
}

export const clearWorkingLayer = (layer: VectorLayerModel): VectorLayerModel =>
  layer.kind === 'working' ? withLayerFeatures(layer, []) : layer;

export const replaceLayer = (layers: readonly VectorLayerModel[], replacement: VectorLayerModel): VectorLayerModel[] =>
  layers.map((layer) => layer.id === replacement.id ? replacement : layer);
